export type TaxReturnDocument = {
  sourceJobId?: string | null;
  fileName: string;
  cloudPath?: string | null;
  uploadedAt?: string;
  fileHash?: string | null;
};

const documentKey = (doc: TaxReturnDocument | null | undefined) =>
  doc ? JSON.stringify([doc.sourceJobId, doc.fileName, doc.cloudPath, doc.uploadedAt, doc.fileHash]) : null;

/**
 * Completed ingestion identity is independent of the transient polling handle.
 * The server document is the durable reload/navigation authority. A local
 * completion bridges the gap before the analytics query catches up.
 */
export class TaxReturnSource {
  private completed: TaxReturnDocument | null = null;
  private baseline: string | null | undefined;
  private awaitingConfirmation = false;
  private detached = false;

  constructor(readonly scope: string) {}

  clear() {
    this.completed = null;
    this.detached = true;
  }

  complete(jobId: string, doc: TaxReturnDocument, previousDoc?: TaxReturnDocument | null) {
    // Unknown initial query state must not be confused with a known empty
    // document. The first response may have started before ingestion finished.
    this.baseline = previousDoc === undefined ? undefined : documentKey(previousDoc);
    this.completed = { ...doc, sourceJobId: jobId };
    this.detached = false;
    this.awaitingConfirmation = true;
  }

  confirmDocument(freshDoc: TaxReturnDocument | null) {
    if (this.detached || !this.completed) return;
    if (freshDoc?.sourceJobId === this.completed.sourceJobId ||
        (!freshDoc?.sourceJobId && freshDoc?.cloudPath &&
          freshDoc.cloudPath === this.completed.cloudPath && freshDoc.fileName === this.completed.fileName)) {
      this.baseline = documentKey(freshDoc);
      this.awaitingConfirmation = false;
    } else {
      this.clear();
    }
  }

  document(liveDoc?: TaxReturnDocument | null): TaxReturnDocument | null {
    if (this.detached) return null;
    // Undefined means the analytics query has not loaded, not deletion.
    if (liveDoc === undefined) return this.completed;
    if (this.completed) {
      const key = documentKey(liveDoc);
      // Ignore the old query snapshot, but never transfer identity onto a
      // subsequently replaced or deleted document.
      if (key === documentKey(this.completed)) this.baseline = key;
      else if (this.awaitingConfirmation) return this.completed;
      else if (key !== this.baseline) {
        this.clear();
        return null;
      }
    } else if (liveDoc?.sourceJobId) {
      this.completed = liveDoc;
      this.baseline = documentKey(liveDoc);
    }
    return this.completed ?? liveDoc ?? null;
  }
}
